import { type FormContainerOnError, type FormSetup } from '@if/mui'
import { Stack } from '@mui/material'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { useUpdateBanner } from '@src/data/api/merchants-api/merchants-api'
import { BannerDto, BannerSchema } from '@src/data/types/BannerSchema'
import { Merchant } from '@src/data/types/Merchant'
import { AxiosError } from 'axios'
import { useCallback, useMemo } from 'react'
import { FieldValues, UseFormReturn, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSyncFormDefaults } from '../../../EditMerchant/components/useSyncFormDefaults'
import BannerInformationPanel from '../BannerInformationPanel'
import BannerPropertiesOwnershipPanel from '../BannerPropertiesOwnershipPanel'
import BannerReportConfigPanel from '../BannerReportConfigPanel'
import { getBannerDtoFromMerchant } from './bannerDto'

/** Config a tab hook returns; spread onto a `<MultiFormTab>`. Mirrors `MerchantTabConfig<T>`. */
export interface BannerTabConfig<T extends FieldValues> {
  useFormSetup: () => FormSetup<T>
  onSubmit: (values: T, methods: UseFormReturn<T>) => unknown
  onError?: FormContainerOnError
  children: (methods: UseFormReturn<T>) => React.ReactNode
}

const BannerGeneralTabContent = ({ defaults }: { defaults: BannerDto }) => {
  const [featureFlags] = useFeatureFlags()
  const { control, trigger } = useFormContext<BannerDto>()
  useSyncFormDefaults<BannerDto>(defaults)

  return (
    <Stack gap={2}>
      <BannerInformationPanel control={control} />
      <BannerReportConfigPanel control={control} trigger={trigger} />
      {featureFlags?.useCustomFlashPlan && <BannerPropertiesOwnershipPanel control={control} />}
    </Stack>
  )
}

/** General tab config. Save stays on the page; the tab container's Cancel handles navigation back. */
export function useBannerGeneralTab(banner: Merchant): BannerTabConfig<BannerDto> {
  const { t } = useTranslation()
  const [updateBanner] = useUpdateBanner()

  const defaults = useMemo(() => getBannerDtoFromMerchant(banner), [banner])

  const useFormSetup = useCallback(
    (): FormSetup<BannerDto> => ({
      schema: BannerSchema,
      defaultValues: defaults,
    }),
    [defaults],
  )

  const onSubmit = useCallback(
    async (values: BannerDto, methods: UseFormReturn<BannerDto>) => {
      await updateBanner(values)
      methods.reset(values)
    },
    [updateBanner],
  )

  // Generic backend error ({ message } body, kind `unknown`); translate the code
  // it carries. Other kinds (e.g. 409) fall through to the app registry.
  const onError = useMemo<FormContainerOnError>(
    () => ({
      unknown: ({ raw }) => {
        const message = (raw as AxiosError<{ message: string }>).response?.data?.message || 'unknown'
        return { role: 'contextual', message: t(`common.errors.${message}`, 'An error occurred') }
      },
    }),
    [t],
  )

  const children = useCallback(() => <BannerGeneralTabContent defaults={defaults} />, [defaults])

  return { useFormSetup, onSubmit, onError, children }
}
